import React from 'react';
import { Table, Form } from 'react-bootstrap';
import { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from "../helpers/Fetchwrapper";
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';

interface WarehouseInfo {
    id_warehouses: number;
    name: string;
}

interface WarehouseStockData {
    id_warehouses: number;
    opening_stock: number;
    production: number;
    return: number;
    warehouse_transfer_in: number;
    shipment: number;
    warehouse_transfer_out: number;
    closing_stock: number;
}

interface ProductStockRow {
    serial_no: number;
    id_products: number;
    product_name: string;
    pack_size: string;
    warehouses: WarehouseStockData[];
    total_closing_stock: number;
}

interface CategoryStockGroup {
    id_product_categories: number;
    category_name: string;
    products: ProductStockRow[];
}

interface ReportResponse {
    warehouses: WarehouseInfo[];
    categories: CategoryStockGroup[];
}

type StockColumnKey = keyof Pick<WarehouseStockData,
    'opening_stock' | 'production' | 'return' | 'warehouse_transfer_in' | 'shipment' | 'warehouse_transfer_out' | 'closing_stock'>;

interface StockColumnConfig {
    key: StockColumnKey;
    title: string;
    cellStyle: React.CSSProperties;
    headerStyle: React.CSSProperties;
}

interface WarehouseStockReportProps {}

const filterReportData = (data: ReportResponse, productSearch: string): ReportResponse => {
    const search = productSearch.trim().toLowerCase();

    if (!search) {
        return data;
    }

    const categories = data.categories
        .map((category) => ({
            ...category,
            products: category.products.filter((product) =>
                product.product_name.toLowerCase().includes(search)
            ),
        }))
        .filter((category) => category.products.length > 0);

    return {
        ...data,
        categories,
    };
};

const WarehouseStockReport: React.FC<WarehouseStockReportProps> = () => {

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<ReportResponse>({ warehouses: [], categories: [] });
    const [productSearch, setProductSearch] = useState('');
    const [formData, setFormData] = useState({
        idProjects: '',
        idProductCategories: '',
        fromDate: '',
        toDate: '',
        showZeroQuantity: false,
    });

    const [projects, setProjects] = useState<any[]>([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });

    const [productCategories, setProductCategories] = useState<{ label: string; value: string }[]>([]);
    const [productCategory, setProductCategory] = useState<{ label: string; value: string }>({ label: 'All Product Categories', value: '' });

    const filteredReportData = useMemo(
        () => filterReportData(reportData, productSearch),
        [reportData, productSearch]
    );

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            setProject(projects[0]);
            setFormData((prevFormData) => ({
                ...prevFormData,
                idProjects: projects[0].value,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    useEffect(() => {
        if (formData.idProjects) {
            getProductCategories(formData.idProjects);
        } else {
            setProductCategories([{ label: 'All Product Categories', value: '' }]);
            setProductCategory({ label: 'All Product Categories', value: '' });
        }
    }, [formData.idProjects]);

    const getProjects = () => {
        fetch(
            process.env.REACT_APP_API_URL + 'lead/projects',
            getRequestOptions()
        )
            .then((resp) => resp.json())
            .then((resp) => {
                const projectObj = resp.data.map((item: any) => ({
                    ...item,
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectObj);
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            });
    };

    const getProductCategories = async (projectId: string) => {
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'product-categories?id_projects=' + projectId,
                getRequestOptions()
            );
            const data = await response.json();

            if (data && Array.isArray(data)) {
                const categoryOptions = data.map((item: any) => ({
                    label: item.category_name,
                    value: item.id_product_categories,
                }));

                const allOption = { label: 'All Product Categories', value: '' };
                setProductCategories([allOption, ...categoryOptions]);
                setProductCategory(allOption);
                setFormData((prev) => ({ ...prev, idProductCategories: '' }));
            }
        } catch (error) {
            console.error('Error fetching product categories:', error);
        }
    };

    const handleProjectChange = (selectedProject: any) => {
        setProject(selectedProject);
        if (selectedProject) {
            setFormData({ ...formData, idProjects: selectedProject.value, idProductCategories: '' });
        } else {
            setFormData({ ...formData, idProjects: '', idProductCategories: '' });
        }
    };

    const handleProductCategoryChange = (selectedCategory: any) => {
        setProductCategory(selectedCategory);
        if (selectedCategory) {
            setFormData({ ...formData, idProductCategories: selectedCategory.value });
        } else {
            setFormData({ ...formData, idProductCategories: '' });
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = event.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleViewReport = async () => {
        if (!formData.idProjects) {
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('Please select both From Date and To Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/warehouse-stock-report',
                postRequestOptions(formData)
            );
            const data = await response.json();

            if (data.success) {
                setReportData(data.data || { warehouses: [], categories: [] });
                setProductSearch('');
            } else {
                setReportData({ warehouses: [], categories: [] });
                if (data.errors && Array.isArray(data.errors)) {
                    data.errors.forEach((error: string) => toast.error(error, { position: 'bottom-right', autoClose: 4000 }));
                } else {
                    toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
                }
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            toast.error('Error loading report data', { position: 'bottom-right', autoClose: 4000 });
        } finally {
            setLoading(false);
        }
    };

    const getWarehouseData = (product: ProductStockRow, warehouseId: number): WarehouseStockData | undefined => {
        return product.warehouses.find((wh) => wh.id_warehouses === warehouseId);
    };

    const getAllProducts = (): ProductStockRow[] => {
        return filteredReportData.categories.flatMap((category) => category.products);
    };

    const hasWarehouseStockColumnData = (warehouseId: number, columnKey: StockColumnKey): boolean => {
        return getAllProducts().some((product) => {
            const whData = getWarehouseData(product, warehouseId);
            return (whData?.[columnKey] ?? 0) !== 0;
        });
    };

    const getVisibleStockColumnsForWarehouse = (warehouseId: number): StockColumnConfig[] => {
        return stockColumns.filter((column) => hasWarehouseStockColumnData(warehouseId, column.key));
    };

    const hasTotalClosingStockData = (): boolean => {
        return getAllProducts().some((product) => (product.total_closing_stock ?? 0) !== 0);
    };

    const stickyGroupHeaderStyle: React.CSSProperties = {
        position: 'sticky',
        top: 0,
        zIndex: 1022,
        backgroundColor: '#f8f9fa',
        boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.4)',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
    };

    const stickySubHeaderStyle: React.CSSProperties = {
        position: 'sticky',
        top: 38,
        zIndex: 1021,
        backgroundColor: '#f8f9fa',
        boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.4)',
        whiteSpace: 'nowrap',
    };

    const stockInStyle: React.CSSProperties = { color: '#198754' };
    const stockOutStyle: React.CSSProperties = { color: '#dc3545' };
    const closingStockStyle: React.CSSProperties = { color: '#0d6efd' };

    const stockInHeaderStyle: React.CSSProperties = { ...stickySubHeaderStyle, ...stockInStyle };
    const stockOutHeaderStyle: React.CSSProperties = { ...stickySubHeaderStyle, ...stockOutStyle };
    const closingStockHeaderStyle: React.CSSProperties = { ...stickySubHeaderStyle, ...closingStockStyle };
    const totalClosingStockHeaderStyle: React.CSSProperties = { ...stickyGroupHeaderStyle, ...closingStockStyle };

    const stockColumns: StockColumnConfig[] = [
        { key: 'opening_stock', title: 'Opening Stock', cellStyle: stockInStyle, headerStyle: stockInHeaderStyle },
        { key: 'production', title: 'Production', cellStyle: stockInStyle, headerStyle: stockInHeaderStyle },
        { key: 'return', title: 'Return', cellStyle: stockInStyle, headerStyle: stockInHeaderStyle },
        { key: 'warehouse_transfer_in', title: 'Transfer IN', cellStyle: stockInStyle, headerStyle: stockInHeaderStyle },
        { key: 'shipment', title: 'Shipment', cellStyle: stockOutStyle, headerStyle: stockOutHeaderStyle },
        { key: 'warehouse_transfer_out', title: 'Transfer OUT', cellStyle: stockOutStyle, headerStyle: stockOutHeaderStyle },
        { key: 'closing_stock', title: 'Closing Stock', cellStyle: closingStockStyle, headerStyle: closingStockHeaderStyle },
    ];

    const showTotalClosingStockColumn = hasTotalClosingStockData();

    const fixedColumnCount = 3 + (showTotalClosingStockColumn ? 1 : 0);
    const totalWarehouseColumns = filteredReportData.warehouses.reduce(
        (sum, warehouse) => sum + getVisibleStockColumnsForWarehouse(warehouse.id_warehouses).length,
        0
    );
    const totalColumns = fixedColumnCount + totalWarehouseColumns;
    const hasReportData = filteredReportData.categories.some((category) => category.products.length > 0);
    const hasLoadedReport = reportData.warehouses.length > 0 || reportData.categories.length > 0;

    const downloadQueryParams = new URLSearchParams(
        Object.entries(formData).map(([key, value]) => [key, String(value ?? '')])
    );

    const handleDownloadClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (!formData.idProjects) {
            event.preventDefault();
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            event.preventDefault();
            toast.error('Please select both From Date and To Date', { position: 'bottom-right', autoClose: 4000 });
        }
    };

    const renderProductRow = (product: ProductStockRow) => (
        <tr key={product.id_products}>
            <td className='text-center'>{product.serial_no}</td>
            <td className='ps-4'>{product.product_name}</td>
            <td>{product.pack_size}</td>
            {reportData.warehouses.map((warehouse) => {
                const whData = getWarehouseData(product, warehouse.id_warehouses);
                const visibleColumns = getVisibleStockColumnsForWarehouse(warehouse.id_warehouses);
                return (
                    <React.Fragment key={`${product.id_products}-${warehouse.id_warehouses}`}>
                        {visibleColumns.map((column) => (
                            <td
                                key={`${product.id_products}-${warehouse.id_warehouses}-${column.key}`}
                                className={`text-end${column.key === 'closing_stock' ? ' fw-bold' : ''}`}
                                style={column.cellStyle}
                            >
                                {numberFormat(whData?.[column.key] ?? 0)}
                            </td>
                        ))}
                    </React.Fragment>
                );
            })}
            {showTotalClosingStockColumn && (
                <td className='text-end fw-bold' style={closingStockStyle}>{numberFormat(product.total_closing_stock ?? 0)}</td>
            )}
        </tr>
    );

    const getReportTotals = () => {
        const warehouseTotals: Record<number, Partial<Record<StockColumnKey, number>>> = {};
        let grandTotalClosingStock = 0;

        reportData.warehouses.forEach((warehouse) => {
            warehouseTotals[warehouse.id_warehouses] = {};
            getVisibleStockColumnsForWarehouse(warehouse.id_warehouses).forEach((column) => {
                warehouseTotals[warehouse.id_warehouses][column.key] = 0;
            });
        });

        getAllProducts().forEach((product) => {
            grandTotalClosingStock += product.total_closing_stock ?? 0;

            reportData.warehouses.forEach((warehouse) => {
                const whData = getWarehouseData(product, warehouse.id_warehouses);
                getVisibleStockColumnsForWarehouse(warehouse.id_warehouses).forEach((column) => {
                    warehouseTotals[warehouse.id_warehouses][column.key] =
                        (warehouseTotals[warehouse.id_warehouses][column.key] ?? 0) + (whData?.[column.key] ?? 0);
                });
            });
        });

        return { warehouseTotals, grandTotalClosingStock };
    };

    const renderTotalRow = () => {
        const { warehouseTotals, grandTotalClosingStock } = getReportTotals();
        const labelColSpan = 3;

        return (
            <tr className='table-light'>
                <td colSpan={labelColSpan} className='text-end fw-bold'>Total</td>
                {reportData.warehouses.map((warehouse) => {
                    const visibleColumns = getVisibleStockColumnsForWarehouse(warehouse.id_warehouses);
                    return (
                        <React.Fragment key={`total-${warehouse.id_warehouses}`}>
                            {visibleColumns.map((column) => (
                                <td
                                    key={`total-${warehouse.id_warehouses}-${column.key}`}
                                    className='text-end fw-bold'
                                    style={column.cellStyle}
                                >
                                    {numberFormat(warehouseTotals[warehouse.id_warehouses][column.key] ?? 0)}
                                </td>
                            ))}
                        </React.Fragment>
                    );
                })}
                {showTotalClosingStockColumn && (
                    <td className='text-end fw-bold' style={closingStockStyle}>
                        {numberFormat(grandTotalClosingStock)}
                    </td>
                )}
            </tr>
        );
    };

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="productCategory">
                                    <Form.Label>Product Category</Form.Label>
                                    <Select
                                        value={productCategory}
                                        onChange={handleProductCategoryChange}
                                        options={productCategories}
                                        placeholder={productCategory.label}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="From Date"
                                        name='fromDate'
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="To Date"
                                        name='toDate'
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="showZeroQuantity">
                                    <Form.Label className='d-block'>&nbsp;</Form.Label>
                                    <div className="form-check mt-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="showZeroQuantity"
                                            name="showZeroQuantity"
                                            label="Zero Quantity"
                                            checked={formData.showZeroQuantity}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <div className="form-control-sm ps-0 mt-7 d-flex gap-2">
                                    <button className='btn btn-sm btn-primary flex-grow-1 p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        )}
                                        <span>View Report</span>
                                    </button>
                                    <a
                                        className="btn btn-sm btn-success p-3"
                                        href={`${process.env.REACT_APP_BACKEND}warehouse_stock_report_download?${downloadQueryParams.toString()}`}
                                        onClick={handleDownloadClick}
                                        title="Excel Download"
                                    >
                                        <i className='fas fa-download'></i>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12'>
                    {hasLoadedReport && (
                        <div className="mb-3 d-flex justify-content-end">
                            <div className="d-flex align-items-center gap-2">
                                <Form.Label htmlFor="warehouseProductSearch" className="mb-0">Search Product</Form.Label>
                                <Form.Control
                                    id="warehouseProductSearch"
                                    type="text"
                                    size="sm"
                                    placeholder="Type product name"
                                    value={productSearch}
                                    onChange={(event) => setProductSearch(event.target.value)}
                                    style={{ width: '250px' }}
                                />
                            </div>
                        </div>
                    )}
                    <div style={{
                        maxHeight: 'calc(100vh - 300px)',
                        overflowY: 'auto',
                        overflowX: 'auto',
                        position: 'relative',
                    }}>
                        <Table bordered hover style={{ marginBottom: 0, width: '100%' }}>
                            <thead>
                                <tr>
                                    <th className='text-center' rowSpan={2} style={stickyGroupHeaderStyle}>Serial NO</th>
                                    <th className='text-center' rowSpan={2} style={stickyGroupHeaderStyle}>Product Name</th>
                                    <th className='text-center' rowSpan={2} style={stickyGroupHeaderStyle}>Pack Size</th>
                                    {reportData.warehouses.map((warehouse) => {
                                        const visibleColumns = getVisibleStockColumnsForWarehouse(warehouse.id_warehouses);
                                        return visibleColumns.length > 0 ? (
                                            <th
                                                key={warehouse.id_warehouses}
                                                className='text-center'
                                                colSpan={visibleColumns.length}
                                                style={stickyGroupHeaderStyle}
                                            >
                                                {warehouse.name}
                                            </th>
                                        ) : null;
                                    })}
                                    {showTotalClosingStockColumn && (
                                        <th className='text-center' rowSpan={2} style={totalClosingStockHeaderStyle}>Closing Stock</th>
                                    )}
                                </tr>
                                <tr>
                                    {reportData.warehouses.map((warehouse) => {
                                        const visibleColumns = getVisibleStockColumnsForWarehouse(warehouse.id_warehouses);
                                        return (
                                            <React.Fragment key={`sub-${warehouse.id_warehouses}`}>
                                                {visibleColumns.map((column) => (
                                                    <th
                                                        key={`${warehouse.id_warehouses}-${column.key}`}
                                                        className='text-center'
                                                        style={column.headerStyle}
                                                    >
                                                        {column.title}
                                                    </th>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {!hasLoadedReport ? (
                                    <tr>
                                        <td colSpan={totalColumns || 3} className='text-center'>No data available</td>
                                    </tr>
                                ) : !hasReportData ? (
                                    <tr>
                                        <td colSpan={totalColumns || 3} className='text-center'>
                                            {productSearch.trim() ? 'No matching products found' : 'No data available'}
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {filteredReportData.categories.map((category) => (
                                            <React.Fragment key={category.id_product_categories}>
                                                <tr className='table-secondary'>
                                                    <td colSpan={totalColumns} className='fw-bold'>
                                                        {category.category_name}
                                                    </td>
                                                </tr>
                                                {category.products.map((product) => renderProductRow(product))}
                                            </React.Fragment>
                                        ))}
                                        {renderTotalRow()}
                                    </>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WarehouseStockReport;
